// src/api/printful.ts — Printful print-on-demand integration functions

import { Payment } from "square";
import { OrderItem } from "../types/OrderItem";
import { ApiResult } from "../types/ApiResult";
import { PrintfulProduct } from "../types/PrintfulProduct";
import { PrintfulSyncProduct } from "../types/PrintfulSyncProduct";
import { ShippingRateItem } from "../types/ShippingRate";

/** Response shape returned by the Printful create-order endpoint. */
export type PrintfulOrderResponse = {
  id: number;
  status: string;
};

/** Fetch the full Printful product catalog. */
export const getPrintfulCatalog = async (): Promise<
  ApiResult<PrintfulProduct[]>
> => {
  try {
    const response = await fetch("/.netlify/functions/get-printful-catalog", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting Printful catalog:", error);
    return { error: `Error getting Printful catalog: ${error}` };
  }
};

/** Fetch a single Printful sync variant by its sync variant ID. Returns the base catalog variant_id among other fields. */
export const getPrintfulVariant = async (
  syncVariantId: string | number,
): Promise<
  ApiResult<import("../types/PrintfulSyncProduct").PrintfulSyncVariant>
> => {
  try {
    const response = await fetch(
      `/.netlify/functions/get-printful-variant?syncVariantId=${syncVariantId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_FUNCTION_SECRET
            ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
            : {}),
        },
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting Printful variant:", error);
    return { error: `Error getting Printful variant: ${error}` };
  }
};

/** Fetch a single Printful sync-product (maps Square variants → Printful variants). */
export const getPrintfulSyncCatalogItem = async (
  productId: string,
): Promise<ApiResult<PrintfulSyncProduct>> => {
  try {
    const response = await fetch(
      `/.netlify/functions/get-printful-catalog-item?productId=${productId}`,
      { method: "GET", headers: { "Content-Type": "application/json" } },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting Printful item:", error);
    return { error: `Error getting Printful item: ${error}` };
  }
};

/**
 * Submit an order to Printful for fulfillment.
 * In development, routes to the Printful test store (via REACT_APP_PRINTFUL_TEST_API_KEY)
 * with confirm:false so orders appear as drafts and are never fulfilled.
 */
export const createPrintfulOrder = async (
  orderItems: OrderItem[],
  contactInfo: Payment,
  phone: string,
  selectedShipping: ShippingRateItem,
  /** Square order ID stored as external_id on the Printful order so the
   *  Printful shipment webhook can look up the Square order later. */
  squareOrderId?: string,
  authToken?: string,
): Promise<ApiResult<PrintfulOrderResponse>> => {
  if (!orderItems || orderItems.length <= 0) return null;
  if (!contactInfo) return null;

  const lineItems = orderItems.map((item) => ({
    variantId: item.variation.itemVariationData?.sku?.split("_")?.[1],
    // Square catalog variation id. The backend uses this to verify the SKU's
    // sync_variant_id actually maps back to THIS variation (external_id) in the
    // pinned Printful store, so a mis-synced SKU fails loudly instead of
    // silently fulfilling the wrong design. See create-printful-order.js.
    variationId: item.variation.id,
    quantity: item.quantity,
  }));

  try {
    const response = await fetch("/.netlify/functions/create-printful-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({
        lineItems,
        contactInfo,
        phone,
        shipping: selectedShipping?.id || "STANDARD",
        squareOrderId,
      }),
    });
    return response.json();
  } catch (error) {
    console.error("Error creating Printful order:", error);
    return { error: `Error creating Printful order: ${error}` };
  }
};

/**
 * Cancel a Printful order by the Square order ID that was stored as
 * external_id when the Printful order was created.
 * Returns null if no squareOrderId is provided.
 */
export const cancelPrintfulOrder = async (
  squareOrderId: string,
): Promise<
  | (ApiResult<PrintfulOrderResponse | null> & {
      notFound?: boolean;
      inProduction?: boolean;
    })
  | null
> => {
  if (!squareOrderId) return null;
  try {
    const response = await fetch("/.netlify/functions/cancel-printful-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ squareOrderId }),
    });
    return response.json();
  } catch (error) {
    console.error("Error canceling Printful order:", error);
    return { error: `Error canceling Printful order: ${error}` };
  }
};
